/**
 * React component for resource display banner.
 * Displays currency/resource counters at the top of the screen.
 */

import { useState, useEffect } from 'react';
import { useGameStateStore, useEconomyStore } from '@shared/stores';

/**
 * Format game time in MM:SS format.
 */
function formatGameTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Income indicator that shows "+xx income" with fade in/out animation.
 */
function IncomeIndicator({ amount }: { amount: number }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Hide after 1 second total (animation handles fade)
    const timer = setTimeout(() => {
      setVisible(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <span
      className="ml-2 text-green-400 font-semibold animate-income-fade"
      style={{
        animation: 'incomeFade 1s ease-in-out forwards',
      }}
    >
      +{amount} income
    </span>
  );
}

export default function ResourceDisplay() {
  const { pauseGame, gameTime } = useGameStateStore();
  const { currency, lastIncome, lastIncomeTimestamp } = useEconomyStore();
  const [incomeKey, setIncomeKey] = useState<number | null>(null);

  // Track when new income arrives to trigger fresh animation
  useEffect(() => {
    if (lastIncomeTimestamp !== null && lastIncome !== null && lastIncome > 0) {
      setIncomeKey(lastIncomeTimestamp);
    }
  }, [lastIncomeTimestamp, lastIncome]);

  return (
    <>
      {/* CSS for income fade animation */}
      <style>{`
        @keyframes incomeFade {
          0% { opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
      `}</style>
      <div
        className="w-full bg-gray-800 bg-opacity-90 flex items-center justify-between px-4 py-2 flex-shrink-0 relative"
        data-top-bar
      >
        <div className="flex items-center gap-4">
          <div className="text-white font-semibold flex items-center">
            <span className="text-gray-300">Currency: </span>
            <span className="text-yellow-400">{currency}</span>
            {incomeKey !== null && lastIncome !== null && lastIncome > 0 && (
              <IncomeIndicator key={incomeKey} amount={lastIncome} />
            )}
          </div>
        </div>
        {/* Centered timer */}
        <div className="absolute left-1/2 transform -translate-x-1/2 text-white font-mono text-lg font-semibold">
          {formatGameTime(gameTime)}
        </div>
        <button
          onClick={pauseGame}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors text-sm font-semibold"
          aria-label="Pause game"
        >
          Menu
        </button>
      </div>
    </>
  );
}
